<script>
    import { getContext, setContext } from 'svelte';
    import { derived, writable } from 'svelte/store';
    import { json } from 'd3-fetch';

    // --- Properties

    export let databaseKey;
    const { retrieve, query, aql, db } = getContext(databaseKey);

    // --- Database managment
    const uuid = '-';
    const collectionNames = [
        'jb_cars',
        'jb_car_stops',
        'jb_poi',
        'jb_creditcards',
        'jb_transactions',
        'jb_matched_transactions',
        'jb_locations',
        'jb_car2cc',
    ];
    const baseCollections = collectionNames.reduce((map, name) => {
        map[name] = db.collection(name);
        return map;
    }, {});
    const workingCollections = collectionNames.reduce((map, name) => {
        map[name] = db.collection(`${name}_${uuid}`);
        return map;
    }, {});

    async function createWorkingCollections() {
        return Promise.all(
            collectionNames.map(async (name) => {
                // await workingCollections[name].drop();
                // await workingCollections[name].create();
                await query(aql`
                FOR d IN ${workingCollections[name]}
                    REMOVE d IN ${workingCollections[name]}
            `);
                return query(aql`
                FOR d IN ${baseCollections[name]}
                    INSERT d IN ${workingCollections[name]}
            `);
            })
        );
    }
    createWorkingCollections();

    async function writeChanges() {
        console.warn('saving is disabled');
    }

    // --- Loading data

    const streets = writable({});
    json('./Abila.geojson').then((res) => {
        $streets = res;
    });
    const carStops = retrieve(
        aql`
        FOR s in ${baseCollections['jb_car_stops']}
            RETURN s
    `,
        (res) =>
            res.reduce((map, stop) => {
                map[stop._key] = stop;
                return map;
            }, {})
    );
    const creditcards = retrieve(
        aql`
        FOR cc in ${baseCollections['jb_creditcards']}
            RETURN cc
        `,
        (res) =>
            res.reduce((map, cc) => {
                map[cc.ccnum] = cc;
                return map;
            }, {})
    );
    const cars = retrieve(
        aql`
        FOR car in ${baseCollections['jb_cars']}
            RETURN car
        `,
        (res) =>
            res.reduce((map, car) => {
                map[car.id] = car;
                return map;
            }, {})
    );
    const pointsOfInterest = retrieve(
        aql`
        FOR p in ${baseCollections['jb_poi']}
            RETURN p
        `,
        (res) =>
            res.reduce((map, poi) => {
                map[poi._key] = poi;
                return map;
            }, {})
    );
    const locations = retrieve(
        aql`
        FOR l in ${baseCollections['jb_locations']}
            RETURN l
    `,
        (res) =>
            res.reduce((map, l) => {
                map[l.name] = l;
                return map;
            }, {})
    );
    const poiTypeDomain = ['Home', 'Office', 'Store', 'Cafe', 'Other'];

    const recallThreshold = writable(0.17);
    const precisionThreshold = writable(0.05);
    const matches_all = retrieve(aql`
        FOR m in ${baseCollections['jb_car2cc']}
            SORT m.num_matches DESC, m.recall DESC
            RETURN m
    `);
    const sortDescending = writable(false);
    const sortField = writable(null);
    const matches = derived(
        [
            matches_all,
            recallThreshold,
            precisionThreshold,
            sortField,
            sortDescending,
        ],
        ([
            $matches_all,
            $recallThreshold,
            $precisionThreshold,
            $sortField,
            $sortDescending,
        ]) => {
            return sortMatches(
                $matches_all.filter(
                    (m) =>
                        m.precision > $precisionThreshold &&
                        m.recall > $recallThreshold
                ),
                $sortField,
                $sortDescending
            );
        }
    );
    const cc2car = derived(matches, ($matches) =>
        $matches.reduce((map, m) => {
            map[m.ccnum] = m.ccnum in map ? [...map[m.ccnum], m.car] : [m.car];
            return map;
        }, {})
    );
    const car2cc = derived(matches, ($matches) =>
        $matches.reduce((map, m) => {
            map[m.car] = m.car in map ? [...map[m.car], m.ccnum] : [m.ccnum];
            return map;
        }, {})
    );
    const orderCC = derived(matches, ($matches) => [
        ...new Set($matches.map((d) => d.ccnum)),
    ]);
    const orderCar = derived(matches, ($matches) => [
        ...new Set($matches.map((d) => d.car)),
    ]);

    function sortMatches(matches, field, order) {
        if (!field) {
            return matches;
        }
        if (order) {
            return matches.sort((a, b) => b[field] - a[field]);
        }
        return matches.sort((a, b) => a[field] - b[field]);
    }

    // --- Helpers
    async function computeMatches() {
        console.log('requesting matches');
        await query(aql`
            FOR m IN ${workingCollections['jb_car2cc']}
                REMOVE m IN ${workingCollections['jb_car2cc']}
        `);
        const res = await query(
            aql`
        FOR s IN ${workingCollections['jb_car_stops']}
            FILTER s.state == 'accepted'
            FILTER s.duration < 8*3600
            LET poi = DOCUMENT(${workingCollections['jb_poi'].name}, concat(s.lat, '_', s.long))
            FILTER !(poi.type IN ['Home', 'Office'])
            
            // Use or find matching transactions
            LET matched_ts = LENGTH(s.transactions) > 0 ? s.transactions : (
                FOR t in ${workingCollections['jb_transactions']}
                    FILTER t.time_of_day > 0
                    FILTER !(t.used_cc == '' && t.time_of_day == 0)
                    FILTER s.start.timestamp < t.timestamp
                    FILTER s.stop.timestamp > t.timestamp
                    FILTER LENGTH(poi.locations) > 0 ? t.location in poi.locations : true
                    LET loc = DOCUMENT(jb_locations, CRC32(t.location))
                    FILTER LENGTH(loc.places) > 0 ? concat(s.lat, '_', s.long) in loc.places : true
                    LET cc = DOCUMENT(${workingCollections['jb_creditcards'].name}, concat('c', t.used_cc))
                    RETURN {t, cc}
            )
            
            FOR m IN matched_ts
                COLLECT carID=s.car, ccnum=m.cc.ccnum into g = {s: s._key, t: m.t, cc: m.cc}

                LET car = DOCUMENT(${workingCollections['jb_cars'].name}, concat(carID))
                LET num_stops = car.num_transaction_stops
                LET num_transactions = g[0].cc.n_transactions
                
                LET num_matches = LENGTH(g)
                LET precision = num_matches / num_stops
                LET recall = num_matches / num_transactions
                
                SORT recall DESC, num_matches DESC, precision DESC
                LET m = {
                    _key: concat(carID, '_', ccnum),
                    car: carID,
                    ccnum: ccnum,
                    precision,
                    recall,
                    num_matches,
                    num_stops,
                    num_transactions,
                    matches: g[*],
                }
                INSERT m IN ${workingCollections['jb_car2cc']}
                RETURN m
        `,
            matches_all
        );
        alert('done');
        return res;
    }

    // --- Editing functions

    async function editPOI(type, location) {
        if (!$focus.poi) {
            return;
        }
        const actions = [];
        // update number of stops considered for transactions for each car
        // that stopped at this POI.
        const home_or_office = ['Home', 'Office'];
        const was_home_or_office = home_or_office.includes($focus.poi.type);
        const becomes_home_or_office = home_or_office.includes(type);
        if (!was_home_or_office && becomes_home_or_office) {
            actions.push(
                query(aql`
            FOR s in ${workingCollections['jb_car_stops']}      
                FILTER s.lat == ${$focus.poi.lat}
                FILTER s.long == ${$focus.poi.long}
                LET car = DOCUMENT(CONCAT(${workingCollections['jb_cars'].name}, '/', s.car))
                UPDATE car WITH { num_transaction_stops: car.num_transaction_stops - 1 } IN ${workingCollections['jb_cars']}
            `)
            );
        }
        if (was_home_or_office && !becomes_home_or_office) {
            actions.push(
                query(aql`
            FOR s in ${workingCollections['jb_car_stops']}        
                FILTER s.lat == ${$focus.poi.lat}
                FILTER s.long == ${$focus.poi.long}
                LET car = DOCUMENT(CONCAT(${workingCollections['jb_cars'].name}, '/', s.car))
                UPDATE car WITH { num_transaction_stops: car.num_transaction_stops + 1 } IN ${workingCollections['jb_cars']}
            `)
            );
        }

        // Update the poi locally and in the db
        $focus.poi.type = type;
        $focus.poi.locations = location.split('; ');
        if ($focus.poi.locations === '') {
            $focus.poi.locatoins = [];
        }
        $pointsOfInterest = $pointsOfInterest;
        actions.push(
            query(aql`
            UPDATE {_key: ${$focus.poi._key}} WITH {
                type: ${type}, 
                locations: ${$focus.poi.locations},
            } IN ${workingCollections['jb_poi']}
        `)
        );
        // computeMatches();
        return Promise.all(actions);
    }

    async function editMatch(match, skipRemove = false) {
        const stop = $carStops[match.s];
        const transaction = match.t;
        const poi = match.poi;
        const cc = match.cc;
        const location = $locations[transaction.location];
        const alreadyAccepted =
            stop.transactions.filter((tc) => {
                return tc.t._key === transaction._key;
            }).length > 0;
        // console.log('alreadyAccepted: ', alreadyAccepted);

        if (alreadyAccepted) {
            if (skipRemove) {
                // console.log('skipping removing match');
                return;
            } else {
                // console.log('removing this match');
            }
            // Remove transaction from stop
            stop.transactions = stop.transactions.filter(
                (tc) => tc.t._key !== transaction._key
            );
            // Update stop
            await query(aql`
                UPDATE {_key: ${stop._key}} WITH {transactions: ${stop.transactions}} IN ${workingCollections['jb_car_stops']}
            `);

            // Remove stop from transaction
            transaction.stop = null;
            // Update transaction
            await query(aql`
                LET t = DOCUMENT(concat(${workingCollections['jb_matched_transactions'].name}, '/', ${transaction._key}))
                UPDATE t WITH {stop: null} IN ${workingCollections['jb_matched_transactions']}
            `);
            await query(aql`
                LET t = DOCUMENT(concat(${workingCollections['jb_matched_transactions'].name}, '/', ${transaction._key}))
                INSERT t IN ${workingCollections['jb_transactions']}
                REMOVE t IN ${workingCollections['jb_matched_transactions']}
            `);

            // Update poi locations
            await query(aql`
                LET locations = (
                    FOR t in ${workingCollections['jb_matched_transactions']}
                        LET s = DOCUMENT(${workingCollections['jb_car_stops'].name}, CONCAT(t.stop))
                        LET poi = DOCUMENT(${workingCollections['jb_poi'].name}, CONCAT(s.lat, '_', s.long))
                        FILTER poi._key == ${poi._key}
                        RETURN DISTINCT t.location
                )
                UPDATE {_key: ${poi._key}} WITH {locations} IN ${workingCollections['jb_poi']}
            `);

            // Update location pois
            await query(aql`
                LET pois = (
                    FOR t in ${workingCollections['jb_matched_transactions']}
                        FILTER t.location == ${location.name}
                        LET s = DOCUMENT(CONCAT(${workingCollections['jb_car_stops'].name}, '/', t.stop))
                        LET poi = DOCUMENT(concat(${workingCollections['jb_poi'].name}, '/', s.lat, '_', s.long))
                        RETURN DISTINCT poi._key
                )
                UPDATE {_key: ${location._key}} WITH {places: pois} IN ${workingCollections['jb_locations']}
            `);
        } else {
            // console.log('adding match');
            // add location to place
            if (!poi.locations.includes(transaction.location)) {
                poi.locations.push(transaction.location);
                // Update poi
                await query(aql`
                    UPDATE {_key: ${poi._key}} WITH {locations: ${poi.locations}} IN ${workingCollections['jb_poi']}
                `);
            }
            // Add place to location
            if (!location.places.includes(poi._key)) {
                location.places.push(poi._key);
                await query(aql`
                    UPDATE {_key: ${location._key}} WITH {places: ${location.places}} IN ${workingCollections['jb_locations']}
                `);
            }
            // add stop to transaction
            transaction.stop = stop._key;
            // Update transaction
            await query(aql`
                LET t = DOCUMENT(concat(${workingCollections['jb_transactions'].name}, '/', ${transaction._key}))
                INSERT MERGE(t, {stop: ${transaction.stop}})  IN ${workingCollections['jb_matched_transactions']}
            `);
            await query(aql`
                LET t = DOCUMENT(concat(${workingCollections['jb_transactions'].name}, '/', ${transaction._key}))
                REMOVE t IN ${workingCollections['jb_transactions']}
            `);
            // add transaction to stop
            stop.transactions.push({ t: transaction, cc });
            // Update stop
            await query(aql`
                UPDATE {_key: ${stop._key}} WITH {transactions: ${stop.transactions}} IN ${workingCollections['jb_car_stops']}
            `);
        }
    }

    // --- Graph traversal functions

    function _traverseCar(state, car, k = 1) {
        state.car[car.id] = car;
        car.stops.forEach((i) => {
            const stop = $carStops[i];
            const poi = `${stop.lat}_${stop.long}`;
            if (!(poi in state.poi)) {
                state.poi[poi] = 1;
            } else {
                ++state.poi[poi];
            }
        });
        if (k > 0) {
            $car2cc[car.id].forEach((ccnum) => {
                if (!(ccnum in state.cc)) {
                    state = _traverseCC(state, $creditcards[ccnum], k - 1);
                }
            });
        }
        return state;
    }

    function _traverseCC(state, cc, k = 1) {
        state.cc[cc.ccnum] = cc;
        if (k > 0) {
            $cc2car[cc.ccnum].forEach((carID) => {
                if (!(carID in state.car)) {
                    state = _traverseCar(state, $cars[carID], k - 1);
                }
            });
        }
        return state;
    }

    // --- Hovering

    // Maps by key of hovered entities
    const emptyHoverState = () => {
        return {
            poi: {},
            car: {},
            cc: {},
            poi_key: '',
            car_key: '',
            cc_key: '',
        };
    };
    const hover = writable(emptyHoverState());

    function hoverCar(car) {
        const state = _traverseCar(emptyHoverState(), car);
        state.car_key = car.id;
        $hover = state;
    }

    function unhoverCar(car) {
        if ($hover.car_key === car.id) {
            $hover = emptyHoverState();
        }
    }

    function hoverCC(cc) {
        const state = _traverseCC(emptyHoverState(), cc);
        state.cc_key = cc.ccnum;
        $hover = state;
    }

    function unhoverCC(cc) {
        if ($hover.cc_key === cc.ccnum) {
            $hover = emptyHoverState();
        }
    }

    function hoverMatch(match) {
        const state = _traverseCar(emptyHoverState(), $cars[match.car]);
        state.match_key = match._key;
        $hover = state;
    }

    function unhoverMatch(match) {
        if ($hover.match_key === match._key) {
            $hover = emptyHoverState();
        }
    }

    // --- Selection

    // Maps by key of selected entities
    const emptySelectionState = () => {
        return {
            poi: {},
            car: {},
            cc: {},
        };
    };
    const selected = writable(emptySelectionState());

    const selectedPOIs = derived(selected, ($selected) => {
        return Object.keys($selected.poi);
    });
    const selectedCars = derived(selected, ($selected) => {
        return Object.keys($selected.car);
    });
    const selectedCCs = derived(selected, ($selected) => {
        return Object.keys($selected.cc);
    });

    function toggleCarSelection(car) {
        if (car.id in $selected.car) {
            if (Object.keys($selected.car).length == 1) {
                $selected = emptySelectionState();
            } else {
                delete $selected.car[car.id];
                car.stops.forEach((i) => {
                    const stop = $carStops[i];
                    const poi = `${stop.lat}_${stop.long}`;
                    if (--$selected.poi[poi] === 0) {
                        delete $selected.poi[poi];
                    }
                });
                $selected = $selected;
            }
        } else {
            $selected = _traverseCar($selected, car);
        }
    }

    function toggleCCSelection(cc) {
        if (cc.ccnum in $selected.cc) {
            if (Object.keys($selected.cc).length == 1) {
                $selected = emptySelectionState();
            } else {
                delete $selected.cc[cc.ccnum];
                $selected = $selected;
            }
        } else {
            $selected = _traverseCC($selected, cc);
        }
    }

    // --- Transactions
    const selectedTransactions = writable([]);
    $: filterTransactions($selectedCCs);

    function filterTransactions(selectedCCs) {
        query(
            aql`
        LET selectedCCs = ${selectedCCs}
        LET unmatched = (
            FOR t in ${workingCollections['jb_transactions']}
                FILTER (TO_STRING(t.used_cc) IN selectedCCs) || 
                    (t.used_cc == '' && LENGTH(INTERSECTION(t.matched_ccnums, selectedCCs)) > 0)
                RETURN t
        )
        LET matched = (
            FOR t in ${workingCollections['jb_matched_transactions']}
                FILTER (TO_STRING(t.used_cc) IN selectedCCs) || 
                    (t.used_cc == '' && LENGTH(INTERSECTION(t.matched_ccnums, selectedCCs)) > 0)
                RETURN t
        )

        FOR t IN UNION(unmatched, matched)
            RETURN t
        `,
            selectedTransactions
        );
    }

    const selectedMatches = derived(
        [matches, selectedCCs, selectedCars],
        ([$matches, $selectedCCs, $selectedCars]) =>
            $matches
                .filter(
                    (m) =>
                        $selectedCars.includes(`${m.car}`) &&
                        $selectedCCs.includes(`${m.ccnum}`)
                )
                .map((m) => m.matches)
                .flat()
                .map((m) => {
                    const s = $carStops[m.s];
                    const poi = $pointsOfInterest[`${s.lat}_${s.long}`];
                    // const type = poiTypeDomain.includes(poi.type) ? poi.type : 'Other';
                    return { ...m, poi };
                })
    );

    // --- Focus

    // single entities which can be edited
    const focusSelected = writable(false);
    const focus = writable({
        poi: null,
    });

    function setPOIFocus(poi) {
        $focus.poi = poi;
    }

    function isFocusPOI(state, key) {
        return state.poi && state.poi._key == key;
    }

    // --- Active entities

    // combines hover and selection state to indicate which items should be
    // highlighted visually
    const activePOI = derived(
        [pointsOfInterest, selected, hover],
        ([$pointsOfInterest, $selected, $hover]) => {
            return Object.keys($pointsOfInterest).reduce((map, poi) => {
                const some_selected = Object.keys($selected.poi).length > 0;
                const this_selected = some_selected && poi in $selected.poi;

                const some_hovered = Object.keys($hover.poi).length > 0;
                const this_hovered = some_hovered && poi in $hover.poi;

                map[poi] =
                    this_selected ||
                    (!some_selected && (this_hovered || !some_hovered));
                return map;
            }, {});
        }
    );

    const activeMatch = derived(
        [matches, selected, hover],
        ([$matches, $selected, $hover]) => {
            return $matches.map((match) => {
                const some_selected = Object.keys($selected.car).length > 0;
                const some_hovered = Object.keys($hover.car).length > 0;

                const this_selected =
                    some_selected &&
                    match.car in $selected.car &&
                    match.ccnum in $selected.cc;
                const this_hovered =
                    some_hovered &&
                    match.car in $hover.car &&
                    match.ccnum in $hover.cc;

                return (
                    this_selected ||
                    (!some_selected && (this_hovered || !some_hovered))
                );
            });
        }
    );

    const activeCar = derived(
        [cars, selected, hover],
        ([$cars, $selected, $hover]) => {
            return Object.keys($cars).reduce((map, id) => {
                const some_selected = Object.keys($selected.car).length > 0;
                const this_selected = some_selected && id in $selected.car;

                const some_hovered = Object.keys($hover.car).length > 0;
                const this_hovered = some_hovered && id in $hover.car;

                map[id] =
                    this_selected ||
                    (!some_selected && (this_hovered || !some_hovered));
                return map;
            }, {});
        }
    );

    const activeCC = derived(
        [creditcards, selected, hover],
        ([$creditcards, $selected, $hover]) => {
            return Object.keys($creditcards).reduce((map, ccnum) => {
                const some_selected = Object.keys($selected.cc).length > 0;
                const this_selected = some_selected && ccnum in $selected.cc;

                const some_hovered = Object.keys($hover.cc).length > 0;
                const this_hovered = some_hovered && ccnum in $hover.cc;

                map[ccnum] =
                    this_selected ||
                    (!some_selected && (this_hovered || !some_hovered));
                return map;
            }, {});
        }
    );

    // --- Setup context

    const key = {};
    setContext(key, {
        // Data and derivatives
        matches,
        selectedMatches,
        sortField,
        sortDescending,
        recallThreshold,
        precisionThreshold,
        writeChanges,
        cc2car,
        car2cc,
        orderCC,
        orderCar,
        streets,
        carStops,
        creditcards,
        cars,
        pointsOfInterest,
        poiTypeDomain,
        // Interactions
        focus,
        setPOIFocus,
        isFocusPOI,
        editPOI,
        editMatch,
        focusSelected,
        hoverCar,
        unhoverCar,
        hoverCC,
        unhoverCC,
        hoverMatch,
        unhoverMatch,
        toggleCarSelection,
        toggleCCSelection,
        selectedTransactions,
        selectedPOIs,
        selectedCars,
        selectedCCs,
        activePOI,
        activeCar,
        activeCC,
        activeMatch,
        computeMatches,
    });
</script>

<slot {key} />
